import { useMutation, gql } from "@apollo/client";
import { useCallback } from "react";
import { WeekDayEnum } from "./WeekDayEnum.tsx";

const CreateUserGroupMutation = gql`
  mutation createUserGroup(
    $name: String!
    $thinkificBundleId: Int
    $availability: [UserGroupAvailabilityInput!]
    $onboardingLiveRequired: Boolean
    $onboardingLiveBookingWindowDays: Int
  ) {
    createUserGroup(
      name: $name
      thinkificBundleId: $thinkificBundleId
      availability: $availability
      onboardingLiveRequired: $onboardingLiveRequired
      onboardingLiveBookingWindowDays: $onboardingLiveBookingWindowDays
    ) {
      id
      name
    }
  }
`;

export type UserGroupAvailabilityInput = {
  weekDay: WeekDayEnum;
  timeIntervals: { from: number; to: number }[];
  timeIntervalTimezone?: string;
};

export function useCreateUserGroup() {
  const [mutate, { loading }] = useMutation(CreateUserGroupMutation);

  const createUserGroup = useCallback(
    async ({
      name,
      thinkificBundleId,
      availability,
      onboardingLiveRequired,
      onboardingLiveBookingWindowDays,
    }: {
      name: string;
      thinkificBundleId?: number;
      availability?: UserGroupAvailabilityInput[];
      onboardingLiveRequired?: boolean;
      onboardingLiveBookingWindowDays?: number;
    }) => {
      const withTz = availability?.map((a) => ({
        ...a,
        timeIntervalTimezone: a.timeIntervalTimezone ?? "Europe/Rome",
      }));
      const { data } = await mutate({
        variables: {
          name,
          thinkificBundleId,
          availability: withTz,
          onboardingLiveRequired,
          onboardingLiveBookingWindowDays,
        },
      });
      const group = data?.createUserGroup;
      if (!group) throw new Error("User group not created");
      return group;
    },
    [mutate],
  );

  return { createUserGroup, loading };
}
